import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { publicApi } from '../../utils/api';
import { Crown, Calculator } from 'lucide-react';

const peso = (n) => `₱${Math.round(n).toLocaleString()}`;

/** Social proof line used in the marketplace hero. */
export function useMarketplaceStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => { publicApi.get('/api/merchant-partners/stats/public').then(({ data }) => setStats(data)).catch(() => {}); }, []);
  return stats;
}

/** Non-member conversion strip: personal savings estimate → join CTA. */
export function JoinStrip({ stats, loggedIn }) {
  const [spend, setSpend] = useState(5000);
  const [plans, setPlans] = useState([]);
  useEffect(() => { publicApi.get('/api/merchant-partners/plans').then(({ data }) => setPlans(data.plans || [])).catch(() => {}); }, []);
  const proPct = stats?.avg_pro_pct || 10;
  const elitePct = stats?.avg_elite_pct || 15;
  const yearlyPro = spend * 12 * proPct / 100;
  const yearlyElite = spend * 12 * elitePct / 100;
  const pro = plans.find((p) => !/elite|premium/i.test(p.name));
  const elite = plans.find((p) => /elite|premium/i.test(p.name));

  return (
    <div className="mb-8 rounded-2xl border border-[#FFD740]/25 bg-gradient-to-r from-[#FFD740]/[0.07] to-[#00E676]/[0.05] p-5 sm:p-6" data-testid="marketplace-join-strip">
      <div className="flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex-1">
          <p className="text-[#FFD740] text-xs uppercase tracking-widest flex items-center gap-1.5 mb-1"><Crown className="w-4 h-4" />{loggedIn ? "You're on the Free plan" : 'Membership is free'}</p>
          <h2 className="font-outfit text-lg text-white font-semibold" data-testid="join-strip-title">
            {loggedIn ? 'Upgrade to Pro or Elite to unlock these member discounts.' : 'Join AGA free to browse every deal — upgrade to Pro or Elite to get the discounts.'}
          </h2>
          <div className="mt-3 flex items-center gap-3 text-sm text-[#A0B4CC]">
            <Calculator className="w-4 h-4 text-[#00E676]" />
            <span>If you spend</span>
            <input type="range" min={500} max={30000} step={500} value={spend} onChange={(e) => setSpend(Number(e.target.value))}
              className="w-32 accent-[#00E676]" data-testid="join-strip-spend-slider" />
            <span className="text-white font-semibold" data-testid="join-strip-spend">{peso(spend)}/month</span>
          </div>
          <p className="text-sm text-[#A0B4CC] mt-2" data-testid="join-strip-estimate">
            you could save about <b className="text-[#00E5FF]">{peso(yearlyPro)}/yr</b> as Pro or <b className="text-[#FFD740]">{peso(yearlyElite)}/yr</b> as Elite
            {pro?.price && yearlyPro >= pro.price ? <> — Pro <b className="text-[#00E676]">pays for itself within the year</b></> : <>, on top of your other member benefits</>}.
          </p>
          <p className="text-[10px] text-[#5C7599] mt-1">Estimate uses the average partner discount ({proPct}% Pro · {elitePct}% Elite).</p>
        </div>
        <div className="flex flex-col sm:flex-row lg:flex-col gap-2 lg:w-56">
          <Link to={loggedIn ? '/my-plan' : '/register'} className="text-center rounded-full bg-[#00E676] text-[#000B18] font-semibold px-5 py-2.5 hover:bg-[#5BFFA0] transition-colors" data-testid="join-strip-join-btn">
            {loggedIn ? `Upgrade to Pro${pro?.price ? ` · ${peso(pro.price)}` : ''}` : 'Join free'}
          </Link>
          {!loggedIn && pro?.price && <p className="text-center text-[11px] text-[#A0B4CC]">Pro {peso(pro.price)} · unlocks discounts</p>}
          {elite && <p className="text-center text-[11px] text-[#A0B4CC]">Elite {peso(elite.price)} · biggest discounts</p>}
          {!loggedIn && <Link to="/login" className="text-center text-xs text-[#A0B4CC] hover:text-white transition-colors" data-testid="join-strip-login-link">Already a member? Log in</Link>}
        </div>
      </div>
    </div>
  );
}
